import { useEffect, useMemo } from 'react';

import SegmentedControl from '@components/atoms/segmented-control';
import type { PickViewMode } from '@hooks/my-team/useMyTeamPickState';

const VIEW_MODE_ORDER: PickViewMode[] = ['nextGw', 'next3Gw', 'priceChanges'];

const VIEW_MODE_LABELS: Record<PickViewMode, string> = {
  nextGw: 'Next GW',
  next3Gw: 'Next 3 GWs',
  priceChanges: 'Price changes'
};

const VIEW_MODE_DATA_CY: Record<PickViewMode, string> = {
  nextGw: 'view-selector-next-gw',
  next3Gw: 'view-selector-next-3-gw',
  priceChanges: 'view-selector-price-changes'
};

type ViewSelectorProps = {
  viewMode: PickViewMode;
  onViewModeChange: (mode: PickViewMode) => void;
  /**
   * Price changes only start once the season is underway, so the tab is
   * hidden before gameweek one.
   */
  shouldShowPriceChanges?: boolean;
};

export const ViewSelector = ({
  viewMode,
  onViewModeChange,
  shouldShowPriceChanges = true
}: ViewSelectorProps) => {
  const visibleModes = useMemo(
    () =>
      shouldShowPriceChanges
        ? VIEW_MODE_ORDER
        : VIEW_MODE_ORDER.filter((mode) => mode !== 'priceChanges'),
    [shouldShowPriceChanges]
  );

  // If the active mode has just been hidden (e.g. price changes once the
  // league context resolves to pre-season), fall back to the first visible
  // mode. Without this the segmented control has no selected tab and the pick
  // view stays stuck in the now-hidden mode.
  const isViewModeVisible = visibleModes.includes(viewMode);
  useEffect(() => {
    if (!isViewModeVisible) onViewModeChange(visibleModes[0]);
  }, [isViewModeVisible, visibleModes, onViewModeChange]);

  // While the effect above is switching away from a now-hidden mode, indexOf
  // returns -1 for one render. SegmentedControl bails on an out-of-range index
  // and leaves a stale, misplaced pill with no tab highlighted, so fall back to
  // the first visible tab until the parent updates the mode.
  const activeIndex = visibleModes.indexOf(viewMode);
  const selectedIndex = activeIndex === -1 ? 0 : activeIndex;
  const tabLabels = visibleModes.map((mode) => VIEW_MODE_LABELS[mode]);
  const dataCyValues = visibleModes.map((mode) => VIEW_MODE_DATA_CY[mode]);

  return (
    <SegmentedControl
      selectedTab={selectedIndex}
      setSelectedTab={(index: number) => onViewModeChange(visibleModes[index])}
      tabLabels={tabLabels}
      dataCyValues={dataCyValues}
    />
  );
};
